<script setup>
import { computed, onActivated, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import {
  useStore,
  useStoreGetters,
  useMapGetter,
} from 'dashboard/composables/store';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import LiveChatCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/LiveChatCampaign/LiveChatCampaignDialog.vue';
import EditLiveChatCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/LiveChatCampaign/EditLiveChatCampaignDialog.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import LiveChatCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/LiveChatCampaignEmptyState.vue';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();
const { isAdmin } = useAdmin();

const editLiveChatCampaignDialogRef = ref(null);
const confirmDeleteCampaignDialogRef = ref(null);
const selectedCampaign = ref(null);

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);
const hasCampaignLoadError = computed(() => uiFlags.value.fetchError);

const [showLiveChatCampaignDialog, toggleLiveChatCampaignDialog] = useToggle();

// SiteUp (Hub): "Criar" na tela de início de Ações de Marketing chega com
// ?new=1 e já abre o formulário. A query é consumida na chegada (a página
// fica em keep-alive, por isso também em onActivated), para o F5 não reabrir.
const route = useRoute();
const router = useRouter();
const openFromQuery = () => {
  // Sem router (specs montam a página sozinha), não há query a consumir.
  if (String(route?.query?.new || '') !== '1') return;
  const cleaned = { ...route.query };
  delete cleaned.new;
  router?.replace({ query: cleaned });
  // Criar é só de admin (o botão do topo já esconde): o link não abre o formulário.
  if (!isAdmin.value) return;
  toggleLiveChatCampaignDialog(true);
};
onMounted(openFromQuery);
onActivated(openFromQuery);
watch(() => route?.query?.new, openFromQuery);

const liveChatCampaigns = computed(
  () => getters['campaigns/getLiveChatCampaigns'].value
);

const hasNoLiveChatCampaigns = computed(
  () => liveChatCampaigns.value?.length === 0 && !isFetchingCampaigns.value
);

const handleEdit = campaign => {
  selectedCampaign.value = campaign;
  editLiveChatCampaignDialogRef.value.dialogRef.open();
};
const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value.dialogRef.open();
};
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.LIVE_CHAT.HEADER_TITLE')"
    :button-label="t('CAMPAIGN.LIVE_CHAT.NEW_CAMPAIGN')"
    @click="toggleLiveChatCampaignDialog()"
  >
    <template #action>
      <LiveChatCampaignDialog
        v-if="showLiveChatCampaignDialog"
        @close="toggleLiveChatCampaignDialog(false)"
      />
    </template>

    <div
      v-if="isFetchingCampaigns"
      class="flex justify-center items-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <div
      v-else-if="hasCampaignLoadError"
      class="flex flex-col items-center justify-center gap-3 py-10 text-n-slate-11"
      role="alert"
    >
      <p>{{ t('CAMPAIGN.LOAD_ERROR') }}</p>
      <Button
        data-testid="campaign-retry"
        :label="t('CAMPAIGN.RETRY')"
        variant="faded"
        color="slate"
        size="sm"
        @click="store.dispatch('campaigns/get')"
      />
    </div>
    <CampaignList
      v-else-if="!hasNoLiveChatCampaigns"
      :campaigns="liveChatCampaigns"
      is-live-chat-type
      @edit="handleEdit"
      @delete="handleDelete"
    />
    <LiveChatCampaignEmptyState
      v-else
      :title="t('CAMPAIGN.LIVE_CHAT.EMPTY_STATE.TITLE')"
      :subtitle="
        isAdmin
          ? t('CAMPAIGN.LIVE_CHAT.EMPTY_STATE.SUBTITLE')
          : t('CAMPAIGN.VIEW_ONLY_EMPTY_HINT')
      "
    />
    <EditLiveChatCampaignDialog
      ref="editLiveChatCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
  </CampaignLayout>
</template>
